import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Button,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { reservationService } from '../../services/reservationService';
import { vehicleService } from '../../services/vehicleService';
import { fleetService } from '../../services/fleetService';
import { Reservation } from '../../types/reservation';
import { Vehicle } from '../../types/vehicle';
import { Fleet } from '../../types';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FilterListIcon from '@mui/icons-material/FilterList';
import { format } from 'date-fns';

interface ReservationListProps {
  organizationId: string;
}

const ReservationList: React.FC<ReservationListProps> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [vehicles, setVehicles] = useState<Record<string, Vehicle>>({});
  const [fleets, setFleets] = useState<Record<string, Fleet>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Filtres
  const [filters, setFilters] = useState({
    fleetId: '',
    vehicleId: '',
    status: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
  });

  useEffect(() => {
    fetchReservations();
  }, [organizationId]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Récupérer les réservations avec les filtres appliqués
      const reservationsData = await reservationService.getReservations(
        organizationId,
        {
          fleetId: filters.fleetId || undefined,
          vehicleId: filters.vehicleId || undefined,
          status: filters.status as any || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        }
      );

      setReservations(reservationsData);

      // Récupérer les informations sur les véhicules et les flottes
      const vehicleIds = [...new Set(reservationsData.map((r: Reservation) => r.vehicleId))];
      const fleetIds = [...new Set(reservationsData.map((r: Reservation) => r.fleetId))];

      const vehiclesData: Record<string, Vehicle> = {};
      for (const id of vehicleIds) {
        const vehicle = await vehicleService.getVehicle(id);
        if (vehicle) {
          vehiclesData[id] = vehicle;
        }
      }
      setVehicles(vehiclesData);

      const fleetsData: Record<string, Fleet> = {};
      for (const id of fleetIds) {
        const fleet = await fleetService.getFleet(id);
        if (fleet) {
          fleetsData[id] = fleet;
        }
      }
      setFleets(fleetsData);

    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyFilters = () => {
    fetchReservations();
  };

  const resetFilters = () => {
    setFilters({
      fleetId: '',
      vehicleId: '',
      status: '',
      startDate: null,
      endDate: null,
    });
  };

  const handleViewReservation = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    // Ici, on pourrait ouvrir un modal avec les détails de la réservation
  };

  const handleConfirmReservation = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setConfirmDialogOpen(true);
  };

  const handleCancelReservation = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setCancelDialogOpen(true);
  };

  const confirmReservation = async () => {
    if (!selectedReservation) return;
    
    try {
      setActionLoading(true);
      await reservationService.updateReservationStatus(
        selectedReservation.id,
        'confirmed'
      );
      setConfirmDialogOpen(false);
      fetchReservations();
    } catch (err) {
      console.error('Error confirming reservation:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionLoading(false);
    }
  };

  const cancelReservation = async () => {
    if (!selectedReservation) return;
    
    try {
      setActionLoading(true);
      await reservationService.updateReservationStatus(
        selectedReservation.id,
        'cancelled'
      );
      setCancelDialogOpen(false);
      fetchReservations();
    } catch (err) {
      console.error('Error cancelling reservation:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip label={t('reservation:management.status.pending')} color="warning" size="small" />;
      case 'confirmed':
        return <Chip label={t('reservation:management.status.confirmed')} color="success" size="small" />;
      case 'cancelled':
        return <Chip label={t('reservation:management.status.cancelled')} color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatDate = (date: any) => {
    if (!date) return '';
    try {
      return format(date.toDate(), 'dd/MM/yyyy HH:mm');
    } catch (e) {
      return format(new Date(date), 'dd/MM/yyyy HH:mm');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          {t('reservation:management.title')}
        </Typography>
        <Button 
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(!showFilters)}
          variant={showFilters ? "contained" : "outlined"}
        >
          {t('reservation:common.filters')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showFilters && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label={t('reservation:management.filters.fleet')}
                value={filters.fleetId}
                onChange={(e) => handleFilterChange('fleetId', e.target.value)}
                size="small"
              >
                <MenuItem value="">
                  {t('reservation:common.all')}
                </MenuItem>
                {Object.entries(fleets).map(([id, fleet]) => (
                  <MenuItem key={id} value={id}>
                    {fleet.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label={t('reservation:management.filters.vehicle')}
                value={filters.vehicleId}
                onChange={(e) => handleFilterChange('vehicleId', e.target.value)}
                size="small"
              >
                <MenuItem value="">
                  {t('reservation:common.all')}
                </MenuItem>
                {Object.entries(vehicles).map(([id, vehicle]) => (
                  <MenuItem key={id} value={id}>
                    {vehicle.make} {vehicle.model} - {vehicle.licensePlate}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label={t('reservation:management.filters.status')}
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                size="small"
              >
                <MenuItem value="">
                  {t('reservation:common.all')}
                </MenuItem>
                <MenuItem value="pending">
                  {t('reservation:management.status.pending')}
                </MenuItem>
                <MenuItem value="confirmed">
                  {t('reservation:management.status.confirmed')}
                </MenuItem>
                <MenuItem value="cancelled">
                  {t('reservation:management.status.cancelled')}
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label={t('reservation:management.filters.dateRange')}
                value={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label={t('reservation:common.to')}
                value={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="contained" 
                  onClick={applyFilters}
                >
                  {t('reservation:common.apply')}
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={resetFilters}
                >
                  {t('reservation:common.reset')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : reservations.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('reservation:common.noData')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('reservation:common.user')}</TableCell>
                <TableCell>{t('reservation:common.vehicle')}</TableCell>
                <TableCell>{t('reservation:common.fleet')}</TableCell>
                <TableCell>{t('reservation:startDate')}</TableCell>
                <TableCell>{t('reservation:endDate')}</TableCell>
                <TableCell>{t('reservation:common.status')}</TableCell>
                <TableCell>{t('reservation:common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reservations.map((reservation) => (
                <TableRow key={reservation.id}>
                  <TableCell>{reservation.userEmail}</TableCell>
                  <TableCell>
                    {vehicles[reservation.vehicleId] ? 
                      `${vehicles[reservation.vehicleId].make} ${vehicles[reservation.vehicleId].model} - ${vehicles[reservation.vehicleId].licensePlate}` : 
                      reservation.vehicleId
                    }
                  </TableCell>
                  <TableCell>
                    {fleets[reservation.fleetId]?.name || reservation.fleetId}
                  </TableCell>
                  <TableCell>{formatDate(reservation.startDate)}</TableCell>
                  <TableCell>{formatDate(reservation.endDate)}</TableCell>
                  <TableCell>{getStatusChip(reservation.status)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex' }}>
                      <Tooltip title={t('reservation:management.actions.view')}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewReservation(reservation)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {reservation.status === 'pending' && (
                        <>
                          <Tooltip title={t('reservation:management.actions.confirm')}>
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleConfirmReservation(reservation)}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title={t('reservation:management.actions.cancel')}>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleCancelReservation(reservation)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogue de confirmation */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>{t('reservation:management.confirmDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('reservation:management.confirmDialog.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialogOpen(false)} 
            disabled={actionLoading}
          >
            {t('reservation:common.cancel')}
          </Button>
          <Button 
            onClick={confirmReservation} 
            color="primary" 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : t('reservation:common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue d'annulation */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
      >
        <DialogTitle>{t('reservation:management.cancelDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('reservation:management.cancelDialog.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCancelDialogOpen(false)} 
            disabled={actionLoading}
          >
            {t('reservation:common.back')}
          </Button>
          <Button 
            onClick={cancelReservation} 
            color="error" 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : t('reservation:common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReservationList;
